import type { Customer } from '@ctct/domain-layer';
import type { InboundPromoCodesPageStore } from '@ctct/state-client-layer';
import type { UsePartnerPromoCodesParamStoreReturn } from '@ctct/state-server-layer';

import { PartnerPromoCodeService } from '../../PromoCodesManager/services/PartnerPromoCodeService';
import { inboundPromoCodesEventBus } from '../InboundPromoCodesEventBus';
import type { InboundPromoCodesUrlAdapter } from '../types';

export class InboundPromoCodesService {
  constructor(
    private pageStore: InboundPromoCodesPageStore,
    private urlAdapter: InboundPromoCodesUrlAdapter,
    private partnerIdToPromoCode: ReturnType<UsePartnerPromoCodesParamStoreReturn['getPartnerIdToPromoCode']>,
    private customer?: Customer
  ) {}

  public api() {
    return {
      getInboundPromoCodes: () => this.pageStore.getState().inboundPromoCodes,
    };
  }

  public data() {
    return {};
  }

  public handlers() {
    return {};
  }

  /**
   * Reads inbound promo codes (URL params or partner codes) and emits an event
   * so that controllers can coordinate applying them to the correct managers.
   */
  public onMount(): void {
    // 1. Check URL params first
    const { promoCodes } = this.urlAdapter.getPromoCodesParams();

    if (promoCodes.length > 0) {
      const uppercasedPromoCodes = [...new Set(promoCodes.map((code) => code.toUpperCase()))];

      this.pageStore.setState({ inboundPromoCodes: uppercasedPromoCodes });
      inboundPromoCodesEventBus.emit('inboundPromoCodesReceived', uppercasedPromoCodes, { source: 'onMount' });

      return;
    }

    // 2. Fall back to partner codes (requires customer data)
    const partnerPromoCodeService = new PartnerPromoCodeService(this.partnerIdToPromoCode, this.customer);
    const promoCodeForPartner = partnerPromoCodeService.getPromoCodeForPartner();

    if (promoCodeForPartner) {
      const code = promoCodeForPartner.toUpperCase();

      this.pageStore.setState({ inboundPromoCodes: [code] });
      inboundPromoCodesEventBus.emit('inboundPromoCodesReceived', [code], { source: 'onMount' });
    }
  }
}
